import moment from 'moment'
import React from 'react'
import {getMaskedString} from '../../modules/helpers'
import {useSetupContext} from '../../helpers/SetupContext'
import parse from 'html-react-parser'
import unescape from 'lodash/unescape'
import {TComponentInfo} from './_types'
import {RtspPlayer, VkIframe} from '../ComponentStreamPlayer'
import {toDomainUrl} from '../../../../_metronic/helpers'
import ComponentObjectView from '../ComponentObjectView/ComponentObjectView'

const PLUG_IMG = toDomainUrl('media/crm/assets/plug.jpg')
function hasPath(v: any): v is {path: string} {
    return v && typeof v === 'object' && typeof v.path === 'string'
}

function isStreamValue(v: any): v is {type: string; value: string} {
    return v && typeof v === 'object' && 'type' in v && 'value' in v
}

const ComponentInfo: React.FC<TComponentInfo> = ({value, field_type}) => {
    const {context} = useSetupContext()

    const renderPrimitive = (v: any): React.ReactNode => {
        if (v === null || v === undefined) return '—'
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
            return String(v)

        if (v && typeof v === 'object') {
            if ('title' in v && (v as any).title !== undefined) return String((v as any).title)
            if ('value' in v && (v as any).value !== undefined) return String((v as any).value)

            if ('code' in v && (v as any).code !== undefined) return String((v as any).code)
            if ('uri' in v && (v as any).uri !== undefined) return String((v as any).uri)

            try {
                return JSON.stringify(v)
            } catch {
                return '[object]'
            }
        }

        return String(v)
    }

    if (field_type === 'image') {
        let imagePath: string | undefined

        if (Array.isArray(value)) {
            const v = value[0]
            if (typeof v === 'string') imagePath = v
            else if (hasPath(v)) imagePath = v.path
        } else if (typeof value === 'string') {
            imagePath = value
        } else if (hasPath(value)) {
            imagePath = value.path
        }

        if (!imagePath) {
            imagePath = PLUG_IMG
        }

        return (
            <div className='componentInfo'>
                <img
                    src={imagePath}
                    alt='image'
                    style={{borderRadius: '6px'}}
                    onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = PLUG_IMG
                    }}
                />
            </div>
        )
    }

    if (Array.isArray(value) ? value.length : value) {
        switch (field_type) {
            case 'email':
                return (
                    <a href={`mailto:${value}`}>
                        <div className='componentInfo'>{value}</div>
                    </a>
                )
            case 'phone':
            case 'price':
                return (
                    <div className='componentInfo'>
                        {getMaskedString(String(value), field_type, context)}
                    </div>
                )
            case 'date':
                return <div className='componentInfo'>{moment(value).format('DD.MM.YYYY')}</div>
            case 'time':
                return <div className='componentInfo'>{moment(value, 'HH:mm').format('HH:mm')}</div>
            case 'datetime':
                return (
                    <div className='componentInfo'>{moment(value).format('DD.MM.YYYY HH:mm')}</div>
                )
            case 'editor':
                return (
                    <div className='componentInfo'>
                        {parse(unescape((value as string | undefined) ?? ''))}
                    </div>
                )
            case 'list':
                const isEmpty =
                    value === null ||
                    value === undefined ||
                    (Array.isArray(value) && value.length === 0)

                if (isEmpty) {
                    return <div className='componentInfo'>—</div>
                }

                return (
                    <div className='componentInfo_list'>
                        {Array.isArray(value) ? (
                            value.map((item, idx) => <div key={idx}>{renderPrimitive(item)}</div>)
                        ) : (
                            <div>{renderPrimitive(value)}</div>
                        )}
                    </div>
                )
            case 'stream_player':
                let parsed: any = value

                if (typeof value === 'string') {
                    try {
                        parsed = JSON.parse(value)
                    } catch (e) {
                        return <div className='componentInfo'>-</div>
                    }
                }

                if (!isStreamValue(parsed)) {
                    return <div className='componentInfo'>-</div>
                }

                return (
                    <div className='componentInfo'>
                        {parsed.type === 'rtsp' && <RtspPlayer url={parsed.value} />}
                        {parsed.type === 'vk' && <VkIframe code={parsed.value} />}
                    </div>
                )
            case 'object':
                return <ComponentObjectView value={value} />

            case 'smart_list': {
                let rows: any[] = []

                if (value === null || value === undefined) {
                    rows = []
                } else if (Array.isArray(value)) {
                    rows = value
                } else if (typeof value === 'string') {
                    try {
                        const parsed = JSON.parse(value)
                        if (Array.isArray(parsed)) rows = parsed
                        else if (parsed && typeof parsed === 'object') rows = Object.values(parsed)
                    } catch (e) {
                        rows = []
                    }
                } else if (value && typeof value === 'object') {
                    rows = Object.values(value)
                }
                if (!rows.length) {
                    return <div className='componentInfo'>—</div>
                }
                return (
                    <div className='componentInfo_smartList'>
                        {rows.map((row, idx) => {
                            const description =
                                row && typeof row === 'object' && 'description' in row
                                    ? String((row as any).description ?? '')
                                    : ''

                            return (
                                <div key={idx} className='componentInfo_smartListRow'>
                                    {description ? (
                                        <div className='componentInfo_smartListText'>
                                            {description}
                                        </div>
                                    ) : (
                                        <div className='componentInfo_smartListText'>
                                            {renderPrimitive(row)}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )
            }

            default:
                return (
                    <div className='componentInfo'>
                        {Array.isArray(value)
                            ? value.map(
                                  (item, index) => `${item}${index < value.length - 1 ? ', ' : ''}`
                              )
                            : renderPrimitive(value)}
                    </div>
                )
        }
    } else {
        return <div className='componentInfo'>-</div>
    }
}

export default ComponentInfo
